import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Copy, CheckCircle2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
    full_name: "",
    email: "",
    password: "",
    phone: "",
    tier: "SILVER",
    expiry_months: 12,
};

export default function CreateMemberDialog({ open, onOpenChange, onCreated }) {
    const { t, isRTL } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null); // shows temp-password screen
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) { setForm(EMPTY); setError(""); setCreated(null); setCopied(false); }
    }, [open]);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim() || null,
                tier: form.tier,
                expiry_months: Number(form.expiry_months) || 12,
            };
            if (form.password.trim()) payload.password = form.password.trim();
            const res = await adminMembersApi.create(payload);
            setCreated(res);
            onCreated?.(res);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const copyTempPwd = async () => {
        if (!created?.temporary_password) return;
        try {
            await navigator.clipboard.writeText(created.temporary_password);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (_) { /* ignore */ }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg rounded-2xl"
                data-testid="create-member-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                {!created ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t("admin.members.createTitle")}</DialogTitle>
                            <DialogDescription>{t("admin.members.createDescription")}</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 mt-2">
                            <div>
                                <Label htmlFor="m_name">{t("admin.members.fields.fullName")}</Label>
                                <Input
                                    id="m_name"
                                    required
                                    value={form.full_name}
                                    onChange={(e) => update({ full_name: e.target.value })}
                                    className="mt-1.5"
                                    data-testid="create-member-name"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="m_email">{t("admin.members.fields.email")}</Label>
                                    <Input
                                        id="m_email"
                                        type="email"
                                        required
                                        dir="ltr"
                                        value={form.email}
                                        onChange={(e) => update({ email: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-member-email"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="m_phone">{t("admin.members.fields.phone")}</Label>
                                    <Input
                                        id="m_phone"
                                        dir="ltr"
                                        placeholder="+20…"
                                        value={form.phone}
                                        onChange={(e) => update({ phone: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-member-phone"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t("admin.members.fields.tier")}</Label>
                                    <Select value={form.tier} onValueChange={(v) => update({ tier: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="create-member-tier">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="SILVER">{t("admin.members.tier.SILVER")}</SelectItem>
                                            <SelectItem value="GOLD">{t("admin.members.tier.GOLD")}</SelectItem>
                                            <SelectItem value="PLATINUM">{t("admin.members.tier.PLATINUM")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="m_months">{t("admin.members.fields.expiryMonths")}</Label>
                                    <Input
                                        id="m_months"
                                        type="number"
                                        min={1}
                                        max={120}
                                        value={form.expiry_months}
                                        onChange={(e) => update({ expiry_months: e.target.value })}
                                        className="mt-1.5"
                                        data-testid="create-member-months"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="m_pwd">{t("admin.members.fields.password")}</Label>
                                <Input
                                    id="m_pwd"
                                    type="text"
                                    dir="ltr"
                                    placeholder={t("admin.members.fields.passwordPlaceholder")}
                                    value={form.password}
                                    onChange={(e) => update({ password: e.target.value })}
                                    className="mt-1.5"
                                    data-testid="create-member-password"
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {t("admin.members.fields.passwordHint")}
                                </p>
                            </div>

                            {error && (
                                <div
                                    className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                                    data-testid="create-member-error"
                                >
                                    {error}
                                </div>
                            )}

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    data-testid="create-member-cancel"
                                >
                                    {t("common.cancel")}
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="create-member-submit">
                                    {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                                    {t("admin.members.createSubmit")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                ) : (
                    <div data-testid="create-member-success">
                        <DialogHeader>
                            <div className="flex items-center gap-2">
                                <div className="icon-tile h-10 w-10">
                                    <CheckCircle2 className="h-5 w-5" />
                                </div>
                                <div>
                                    <DialogTitle>{t("admin.members.successTitle")}</DialogTitle>
                                    <DialogDescription>{t("admin.members.successDescription")}</DialogDescription>
                                </div>
                            </div>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-xl border border-border bg-muted/30 px-3 py-2">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                        {t("admin.members.fields.memberCode")}
                                    </p>
                                    <p className="font-mono font-semibold text-base mt-0.5" data-testid="success-member-code">
                                        {created.member_code}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-border bg-muted/30 px-3 py-2">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                        {t("admin.members.fields.email")}
                                    </p>
                                    <p className="text-sm mt-0.5 truncate">{created.email}</p>
                                </div>
                            </div>

                            {created.temporary_password && (
                                <div className="rounded-xl border border-primary/30 bg-secondary px-4 py-3">
                                    <p className="text-[11px] uppercase tracking-wide text-primary font-semibold">
                                        {t("admin.members.tempPasswordLabel")}
                                    </p>
                                    <div className="mt-1.5 flex items-center gap-2">
                                        <code
                                            className="flex-1 font-mono text-base text-foreground"
                                            data-testid="success-temp-password"
                                        >
                                            {created.temporary_password}
                                        </code>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="outline"
                                            onClick={copyTempPwd}
                                            data-testid="success-copy-password"
                                            aria-label="Copy password"
                                        >
                                            {copied ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                    <p className="mt-2 text-xs text-muted-foreground">
                                        {t("admin.members.tempPasswordHint")}
                                    </p>
                                </div>
                            )}
                        </div>
                        <DialogFooter className="mt-6">
                            <Button onClick={() => onOpenChange(false)} data-testid="create-member-done">
                                {t("admin.members.done")}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
